import { useCallback, useEffect, useState } from 'react'
import {
  banShop,
  getActiveShopBan,
  liftShopBan,
  type ShopBanRead,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'

interface BanPanelProps {
  shopId: string
  onShopChanged?: () => void
}

const BAN_CATEGORIES = [
  { value: '', label: 'No category' },
  { value: 'fraud', label: 'Fraud' },
  { value: 'counterfeit', label: 'Counterfeit / IP violation' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'csam_or_minors', label: 'CSAM / minor safety' },
  { value: 'tos_violation', label: 'Other ToS violation' },
]

export function BanPanel({ shopId, onShopChanged }: BanPanelProps) {
  const [ban, setBan] = useState<ShopBanRead | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showBanForm, setShowBanForm] = useState(false)
  const [showLiftForm, setShowLiftForm] = useState(false)

  const [reason, setReason] = useState('')
  const [reasonCategory, setReasonCategory] = useState('')
  const [liftedReason, setLiftedReason] = useState('')

  const canEdit = usePermission('shops', 'edit')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const res = await getActiveShopBan(shopId)
    if (res.ok) setBan(res.data ?? null)
    else setError(res.error?.message || 'Failed to load ban')
    setLoading(false)
  }, [shopId])

  useEffect(() => {
    load()
  }, [load])

  const resetBanForm = () => {
    setReason('')
    setReasonCategory('')
    setError(null)
  }

  const handleBan = async () => {
    if (!reason.trim()) {
      setError('Reason is required.')
      return
    }
    setSubmitting(true)
    setError(null)
    const res = await banShop(
      shopId,
      reason.trim(),
      reasonCategory.trim() || undefined,
    )
    setSubmitting(false)
    if (!res.ok) {
      setError(res.error?.message || 'Failed to ban shop.')
      return
    }
    resetBanForm()
    setShowBanForm(false)
    await load()
    onShopChanged?.()
  }

  const handleLift = async () => {
    if (!liftedReason.trim()) {
      setError('Lift reason is required.')
      return
    }
    setSubmitting(true)
    setError(null)
    const res = await liftShopBan(shopId, liftedReason.trim())
    setSubmitting(false)
    if (!res.ok) {
      setError(res.error?.message || 'Failed to lift ban.')
      return
    }
    setLiftedReason('')
    setShowLiftForm(false)
    await load()
    onShopChanged?.()
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">Ban + archive</h4>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Banning captures a frozen snapshot of the shop's public-facing data and sets
          status to suspended. Lifting the ban does not auto-restore status.
        </p>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {ban ? (
        <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-900/10 p-4">
          <div className="mb-2 inline-block rounded-full bg-red-100 dark:bg-red-900/40 px-2.5 py-0.5 text-xs font-medium text-red-700 dark:text-red-300">
            Banned
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <div className="text-gray-500 dark:text-gray-400">Reason</div>
              <div className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                {ban.reason}
              </div>
            </div>
            {ban.reason_category && (
              <div>
                <div className="text-gray-500 dark:text-gray-400">Category</div>
                <div className="font-mono text-xs">{ban.reason_category}</div>
              </div>
            )}
            <div>
              <div className="text-gray-500 dark:text-gray-400">Decided</div>
              <div className="text-xs">{new Date(ban.decided_at).toLocaleString()}</div>
            </div>
            <div className="col-span-2">
              <div className="text-gray-500 dark:text-gray-400">Moderator</div>
              <div className="font-mono text-xs">{ban.moderator_user_id}</div>
            </div>
          </div>

          <details className="mt-3">
            <summary className="cursor-pointer text-sm text-[#7f4f9a] dark:text-[#b58fcc] hover:underline">
              View frozen snapshot
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-white dark:bg-[#1a1520] p-3 text-xs">
              {JSON.stringify(ban.snapshot, null, 2)}
            </pre>
          </details>

          {canEdit && !showLiftForm && (
            <div className="mt-4">
              <CustomButton onClick={() => setShowLiftForm(true)} variant="secondary">
                Lift ban
              </CustomButton>
            </div>
          )}
          {showLiftForm && (
            <div className="mt-4 space-y-2">
              <CustomTextInput
                label="Lift reason (required)"
                value={liftedReason}
                onChange={(e) => setLiftedReason(e.target.value)}
                placeholder="Why is this ban being lifted?"
              />
              <div className="flex gap-2">
                <CustomButton
                  onClick={handleLift}
                  disabled={submitting || !liftedReason.trim()}
                  variant="primary"
                  size="sm"
                >
                  {submitting ? 'Lifting...' : 'Confirm lift'}
                </CustomButton>
                <CustomButton
                  onClick={() => {
                    setLiftedReason('')
                    setShowLiftForm(false)
                  }}
                  variant="secondary"
                  size="sm"
                >
                  Cancel
                </CustomButton>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-4 text-sm text-gray-600 dark:text-gray-300">
          No active ban on this shop.
        </div>
      )}

      {!ban && canEdit && !showBanForm && (
        <CustomButton onClick={() => setShowBanForm(true)} variant="danger">
          Ban shop
        </CustomButton>
      )}

      {showBanForm && (
        <div className="rounded-xl border border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#2a1f35] p-4">
          <h5 className="mb-3 font-medium text-gray-900 dark:text-gray-100">Ban this shop</h5>
          <div className="space-y-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Category (optional)
              </label>
              <select
                value={reasonCategory}
                onChange={(e) => setReasonCategory(e.target.value)}
                className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2.5 text-sm"
              >
                {BAN_CATEGORIES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Reason (required)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Documented reason — referenced in audit log and snapshot."
                rows={4}
                className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2.5 text-sm focus:border-[#503864] focus:outline-none focus:ring-2 focus:ring-[#503864]/20"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <CustomButton
              onClick={handleBan}
              disabled={submitting || !reason.trim()}
              variant="danger"
            >
              {submitting ? 'Banning...' : 'Confirm ban'}
            </CustomButton>
            <CustomButton
              onClick={() => {
                resetBanForm()
                setShowBanForm(false)
              }}
              variant="secondary"
            >
              Cancel
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  )
}
